<template>
  <div id="app">
    <!-- Loading state -->
    <div
      v-if="auth.isLoading.value"
      class="auth-loading"
    >
      <h2>Loading...</h2>
    </div>

    <!-- Main app (show for all users, router handles auth) -->
    <template v-else>
      <!-- Header (only show on authenticated routes or local mode) -->
      <header v-if="showHeader">
        <div class="header-left">
          <HamburgerMenu />
          <h1>MasoDJ</h1>
        </div>
        <div class="header-controls">
          <!-- License expiry badge (local-only mode) -->
          <LicenseBadge />

          <!-- Login/Signup button for unauthenticated cloud mode -->
          <div
            v-if="!auth.isAuthenticated.value && !auth.isLocalMode.value"
            class="auth-buttons-header"
          >
            <button
              class="login-button-header"
              @click="auth.login()"
            >
              Log In / Sign Up
            </button>
          </div>

          <!-- Spotify widget (before user info) -->
          <SpotifyAuthWidget
            v-if="!auth.isLocalMode.value && auth.isAuthenticated.value"
            ref="spotifyAuthWidget"
            :spotify-service="spotifyService"
            @auth-change="handleAuthChange"
          />

          <!-- User info for authenticated users -->
          <div
            v-if="auth.user.value"
            class="user-info"
          >
            <div class="user-buttons">
              <button
                v-if="!auth.isLocalMode.value"
                class="logout-button"
                @click="auth.logout()"
              >
                Logout
              </button>
            </div>
            <span class="user-email">{{ displayedEmail }}</span>
          </div>
        </div>
      </header>

      <!-- Subscription Banner (only on player route when authenticated) -->
      <SubscriptionBanner
        v-if="!auth.isLocalMode.value && $route.name === 'player'"
        @upgrade="handleUpgrade"
        @renew="handleRenew"
        @convert="handleConversion"
      />

      <!-- Router content -->
      <router-view v-slot="{ Component }">
        <component
          :is="Component"
          v-if="Component"
          ref="playerViewRef"
          :show-upgrade-dialog="() => showUpgradeDialog = true"
        />
      </router-view>

      <!-- Modal dialogs -->
      <ConfirmDialog />
      <PromptDialog />
      <UpgradeDialog
        :is-open="showUpgradeDialog"
        :subscriptions-only="upgradeDialogSubscriptionsOnly"
        @close="showUpgradeDialog = false"
      />

      <!-- Copyright footer -->
      <footer class="copyright-footer">
        <span>MasoDJ v{{ version }} &copy; Jesper Högström 2025-2026</span>
      </footer>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { usePlaylistStore } from './stores/playlist';
import { useDialogStore } from './stores/dialogs';
import { useSubscriptionStore } from './stores/subscription';
import { useAuth } from './composables/useAuth';
import { useEntitlement } from './composables/useEntitlement';
import { spotifyServiceKey } from './injection-keys';
import { useViewportResize } from './composables/useViewportResize';
import { logger } from '@maso-dj/shared-types';
import SpotifyAuthWidget from './components/SpotifyAuthWidget.vue';
import ConfirmDialog from './components/ConfirmDialog.vue';
import PromptDialog from './components/PromptDialog.vue';
import SubscriptionBanner from './components/SubscriptionBanner.vue';
import UpgradeDialog from './components/UpgradeDialog.vue';
import HamburgerMenu from './components/HamburgerMenu.vue';
import LicenseBadge from './components/LicenseBadge.vue';
import { inject } from 'vue';
import packageJson from '../package.json';

const version = packageJson.version;

// Inject services
const spotifyService = inject(spotifyServiceKey)!;

// Pinia Store
const store = usePlaylistStore();
const dialogStore = useDialogStore();
const subscriptionStore = useSubscriptionStore();

// Authentication
const auth = useAuth();

// Router (unused but needed for router-view to work)
const _route = useRoute();

// Try to get Auth0 instance (will not exist in local-only builds)
try {
  const auth0 = useAuth0();
  auth.setAuth0Instance(auth0);
} catch {
  // Auth0 not available (local-only build)
  logger.info('Auth0 not available - local-only build');
  auth.setAuth0Instance(null);
}

// Entitlement management (for local-only mode)
const entitlement = useEntitlement();

// Composables
useViewportResize(); // Handle dynamic viewport resizing

// Component refs
const spotifyAuthWidget = ref<InstanceType<typeof SpotifyAuthWidget> | null>(null);
const playerViewRef = ref<{ spotifySearchView?: { hide: () => void } } | null>(null);

// Local state
const isSpotifyAuthenticated = ref(false);
const showUpgradeDialog = ref(false);
const upgradeDialogSubscriptionsOnly = ref(false);

// Displayed email - use entitlement email in local mode if available
const displayedEmail = computed(() => {
  if (auth.isLocalMode.value && entitlement.email.value) {
    return entitlement.email.value;
  }
  return auth.user.value?.email || auth.user.value?.name || 'User';
});

// Show header logic
const showHeader = computed(() => {
  // Always show header - it adapts to show login buttons for unauthenticated users
  // or user info for authenticated users
  return true;
});

// Handle upgrade action from banner (new user, trial user, free tier)
const handleUpgrade = (): void => {
  upgradeDialogSubscriptionsOnly.value = false; // Show all plans
  showUpgradeDialog.value = true;
};

// Handle renew action from banner (expired/canceled subscriber)
const handleRenew = (): void => {
  upgradeDialogSubscriptionsOnly.value = false; // Show all plans including one-time
  showUpgradeDialog.value = true;
};

// Handle conversion from 6-month to recurring subscription
const handleConversion = async (): Promise<void> => {
  try {
    // Open upgrade dialog with only subscription plans (no one-time)
    upgradeDialogSubscriptionsOnly.value = true;
    showUpgradeDialog.value = true;

    // In future iterations, we could:
    // 1. Pre-select the monthly plan in the upgrade dialog
    // 2. Use subscriptionStore.convertToSubscription() for direct conversion
    // 3. Show a specialized conversion dialog
  } catch (error) {
    logger.error('Failed to handle conversion:', error);
  }
};

// Event handlers
const handleAuthChange = async (authenticated: boolean): Promise<void> => {
  logger.debug('handleAuthChange called, authenticated:', authenticated);
  isSpotifyAuthenticated.value = authenticated;
  store.setSpotifyAuthenticated(authenticated);

  if (authenticated) {
    logger.info('User authenticated with Spotify, fetching access token...');
    const token = await spotifyService.getAccessToken();
    logger.debug('Access token received:', token ? 'Yes (length: ' + token.length + ')' : 'No');

    if (token) {
      try {
        logger.info('Initializing Spotify player...');
        await spotifyService.initializePlayer(token);
        logger.info('Spotify player initialized successfully');
      } catch (error) {
        logger.error('Failed to initialize Spotify player:', error);
        // Show error to user
        if (error instanceof Error) {
          alert('Failed to initialize Spotify player: ' + error.message);
        }
      }
    } else {
      logger.error('No access token available');
      alert('Failed to get Spotify access token. Please try logging in again.');
    }
  } else {
    // Hide Spotify search view if it exists
    if (playerViewRef.value?.spotifySearchView) {
      playerViewRef.value.spotifySearchView.hide();
    }
    spotifyService.disconnect();
  }
};

const initialize = async (): Promise<void> => {
  try {
    // Initialize authentication first
    await auth.initialize();

    // Only proceed if authenticated
    if (!auth.isAuthenticated.value) {
      logger.debug('Not authenticated, skipping playlist load');
      return;
    }

    // Wait for token to be available before making API calls
    logger.debug('Ensuring authentication token is available...');
    const token = await auth.getToken();
    if (!token && !auth.isLocalMode.value) {
      logger.error('Authentication token not available after login');
      return;
    }
    logger.debug('Authentication token confirmed');

    // If in local-only mode, load entitlement
    if (auth.isLocalMode.value) {
      logger.info('Loading entitlement for local-only mode...');
      await entitlement.loadEntitlement();
    }

    // Fetch subscription status (works in both network and local-only mode)
    // In local-only mode, this will check for saved entitlements and return appropriate limits
    logger.debug('Fetching subscription status...');
    await subscriptionStore.fetchSubscriptionStatus();

    logger.debug('Loading playlists and restoring last selected...');
    await store.loadLastPlaylistOrFirst();
    logger.debug('Current playlist loaded:', store.currentPlaylistId);

    const isAuth = await spotifyService.isAuthenticated();
    isSpotifyAuthenticated.value = isAuth;
    store.setSpotifyAuthenticated(isAuth);

    if (isAuth) {
      const token = await spotifyService.getAccessToken();
      if (token) {
        try {
          await spotifyService.initializePlayer(token);
        } catch (error) {
          logger.error('Failed to initialize Spotify player:', error);
        }
      }
    } else {
      playerViewRef.value?.spotifySearchView?.hide();
    }
  } catch (error) {
    logger.error('Failed to initialize:', error);
  }
};

onMounted(async () => {

  // Check for URL parameters (but don't show dialogs yet)
  const urlParams = new URLSearchParams(window.location.search);
  const hasCheckout = urlParams.has('checkout');
  const checkoutStatus = urlParams.get('checkout');
  const hasSpotifyAuth = urlParams.has('spotify_auth');
  const hasSpotifyError = urlParams.has('spotify_error');
  const spotifyError = urlParams.get('spotify_error');

  // Initialize auth and load data first
  await initialize();

  // Now handle callbacks after auth is ready
  if (hasSpotifyAuth && urlParams.get('spotify_auth') === 'success') {
    spotifyAuthWidget.value?.refresh();
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  if (hasSpotifyError) {
    logger.error('Spotify authentication error:', spotifyError);
    await dialogStore.alert(
      `Spotify authentication failed: ${spotifyError}`,
      { confirmText: 'OK' }
    );
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  // Handle Stripe checkout callback after auth is ready
  if (hasCheckout) {
    // Clean up URL first (before any navigation or dialogs)
    window.history.replaceState({}, document.title, window.location.pathname);

    if (checkoutStatus === 'success') {
      // Refresh subscription status first
      await subscriptionStore.fetchSubscriptionStatus();

      // Show success message
      await dialogStore.alert(
        'Thank you for subscribing! Your subscription is now active!',
        { confirmText: 'OK' }
      );
    } else if (checkoutStatus === 'cancel') {
      await dialogStore.alert(
        'Subscription cancelled. You can upgrade anytime!',
        { confirmText: 'OK' }
      );
    }
  }
});
</script>
